
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const Settings = () => {
  const { user } = useAuth();

  if (user?.role !== "board") {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-muted-foreground">
          Only board members can access system settings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="grid grid-cols-4 w-full md:w-fit">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>
        
        <div className="mt-6 space-y-6">
          <TabsContent value="general" className="m-0">
            <GeneralSettings />
          </TabsContent>
          
          <TabsContent value="notifications" className="m-0">
            <NotificationSettings />
          </TabsContent>
          
          <TabsContent value="schedule" className="m-0">
            <ScheduleSettings />
          </TabsContent>
          
          <TabsContent value="tasks" className="m-0">
            <TaskSettings />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

const GeneralSettings = () => {
  const [schoolName, setSchoolName] = useState("Sunshine Cooperative Preschool");
  const [address, setAddress] = useState("123 Main St, Anytown, USA");
  const [phone, setPhone] = useState("(555) 123-4567");
  const [email, setEmail] = useState("info@sunshinepreschool.org");

  const saveGeneral = () => {
    toast.success("General settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>General Settings</CardTitle>
        <CardDescription>
          Basic information about your preschool
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="schoolName">School Name</Label>
          <Input
            id="schoolName"
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={saveGeneral}>Save Changes</Button>
      </CardFooter>
    </Card>
  );
};

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    emailReminders: true,
    taskNotifications: true,
    scheduleChanges: true,
    weeklyDigest: false,
    newUserAlerts: true,
    taskOverdue: true,
  });

  const toggleSetting = (settingKey: keyof typeof settings) => {
    setSettings((prev) => ({ ...prev, [settingKey]: !prev[settingKey] }));
  };

  const saveNotifications = () => {
    toast.success("Notification settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Settings</CardTitle>
        <CardDescription>Manage how users receive notifications</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Email Reminders</h4>
              <p className="text-sm text-muted-foreground">
                Send email reminders for upcoming work days
              </p>
            </div>
            <Switch
              checked={settings.emailReminders}
              onCheckedChange={() => toggleSetting("emailReminders")}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Task Notifications</h4>
              <p className="text-sm text-muted-foreground">
                Notify users when they are assigned new tasks
              </p>
            </div>
            <Switch
              checked={settings.taskNotifications}
              onCheckedChange={() => toggleSetting("taskNotifications")}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Schedule Changes</h4>
              <p className="text-sm text-muted-foreground">
                Notify users when their schedule changes
              </p>
            </div>
            <Switch
              checked={settings.scheduleChanges}
              onCheckedChange={() => toggleSetting("scheduleChanges")}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Weekly Digest</h4>
              <p className="text-sm text-muted-foreground">
                Send a weekly summary of activities and tasks
              </p>
            </div>
            <Switch
              checked={settings.weeklyDigest}
              onCheckedChange={() => toggleSetting("weeklyDigest")}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">New User Alerts</h4>
              <p className="text-sm text-muted-foreground">
                Alert admins when new users register
              </p>
            </div>
            <Switch
              checked={settings.newUserAlerts}
              onCheckedChange={() => toggleSetting("newUserAlerts")}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Overdue Task Reminders</h4>
              <p className="text-sm text-muted-foreground">
                Send reminders for overdue tasks
              </p>
            </div>
            <Switch
              checked={settings.taskOverdue}
              onCheckedChange={() => toggleSetting("taskOverdue")}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={saveNotifications}>Save Changes</Button>
      </CardFooter>
    </Card>
  );
};

const ScheduleSettings = () => {
  const saveSchedule = () => {
    toast.success("Schedule settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule Settings</CardTitle>
        <CardDescription>Configure schedule and work day options</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="minHours">Minimum Work Hours per Family</Label>
          <Input id="minHours" type="number" defaultValue={20} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="advanceNotice">Advance Notice for Schedule Changes (days)</Label>
          <Input id="advanceNotice" type="number" defaultValue={7} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="defaultShiftLength">Default Shift Length (hours)</Label>
          <Input id="defaultShiftLength" type="number" defaultValue={4} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="scheduleVisibility">Schedule Visibility</Label>
          <Select defaultValue="month">
            <SelectTrigger id="scheduleVisibility">
              <SelectValue placeholder="Select visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">One Week</SelectItem>
              <SelectItem value="twoWeeks">Two Weeks</SelectItem>
              <SelectItem value="month">One Month</SelectItem>
              <SelectItem value="quarter">Full Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2 pt-2">
          <Switch id="autoReminders" defaultChecked />
          <Label htmlFor="autoReminders">Send automatic reminders for upcoming shifts</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch id="allowSwaps" defaultChecked />
          <Label htmlFor="allowSwaps">Allow parents to swap shifts</Label>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={saveSchedule}>Save Changes</Button>
      </CardFooter>
    </Card>
  );
};

const TaskSettings = () => {
  const saveTaskSettings = () => {
    toast.success("Task settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Task Settings</CardTitle>
        <CardDescription>Configure task management options</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="defaultTaskCategory">Default Task Category</Label>
          <Select defaultValue="classroom">
            <SelectTrigger id="defaultTaskCategory">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="classroom">Classroom</SelectItem>
              <SelectItem value="facilities">Facilities</SelectItem>
              <SelectItem value="administrative">Administrative</SelectItem>
              <SelectItem value="events">Events</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="taskApproval">Task Approval Process</Label>
          <Select defaultValue="teacherApprove">
            <SelectTrigger id="taskApproval">
              <SelectValue placeholder="Select approval process" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="selfReport">Self-Report Completion</SelectItem>
              <SelectItem value="teacherApprove">Teacher Approval Required</SelectItem>
              <SelectItem value="boardApprove">Board Approval Required</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="taskArchiveDays">Auto-Archive Completed Tasks (days)</Label>
          <Input id="taskArchiveDays" type="number" defaultValue={30} />
        </div>
        <div className="flex items-center space-x-2 pt-2">
          <Switch id="allowComments" defaultChecked />
          <Label htmlFor="allowComments">Allow comments on tasks</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch id="allowAttachments" defaultChecked />
          <Label htmlFor="allowAttachments">Allow file attachments on tasks</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch id="parentCreateTask" defaultChecked={false} />
          <Label htmlFor="parentCreateTask">Allow parents to create tasks</Label>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={saveTaskSettings}>Save Changes</Button>
      </CardFooter>
    </Card>
  );
};

export default Settings;
